import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

/**
 * USAMapWidget - Renders a USA choropleth map with states colored by spending
 * Uses react-simple-maps (free, no API key needed)
 * 
 * @param {Array} data - Array of data points with xLabel (state name), yValue (amount), and metadata (stateCode)
 * @param {Object} config - Widget configuration (title, description, etc.)
 * @param {Number} height - Chart height in pixels
 */
export default function USAMapWidget({ data, config, height = 300 }) {
  const [tooltipContent, setTooltipContent] = useState('');

  // Find min/max values for color scaling
  const values = data.map(item => parseFloat(item.yValue));
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);

  // Create a map of state codes to values
  const stateData = {};
  data.forEach(item => {
    try {
      const metadata = item.metadata ? JSON.parse(item.metadata) : {};
      const stateCode = metadata.stateCode || item.xLabel;
      stateData[stateCode] = {
        value: parseFloat(item.yValue),
        label: item.xLabel,
        currency: metadata.currency || '$',
        transactions: item.zValue,
      };
    } catch (err) {
      console.error(`Error parsing data for ${item.xLabel}:`, err);
    }
  });

  // GeoJSON URL for USA states
  const geoUrl = '/us-states-10m.json';

  // Function to get color based on value
  const getColor = (value) => {
    if (!value) return '#E0E0E0'; // Gray for no data
    
    const normalizedValue = ((value - minValue) / (maxValue - minValue)) || 0;
    
    // Color scale: light green -> yellow -> orange -> red
    if (normalizedValue < 0.25) {
      const hue = 120; // Green
      const lightness = 80 - (normalizedValue * 4 * 30); // 80% to 50%
      return `hsl(${hue}, 70%, ${lightness}%)`;
    } else if (normalizedValue < 0.5) {
      const hue = 120 - ((normalizedValue - 0.25) * 4 * 60); // Green to yellow
      return `hsl(${hue}, 70%, 50%)`;
    } else if (normalizedValue < 0.75) {
      const hue = 60 - ((normalizedValue - 0.5) * 4 * 30); // Yellow to orange
      return `hsl(${hue}, 70%, 50%)`;
    } else {
      const hue = 30 - ((normalizedValue - 0.75) * 4 * 30); // Orange to red
      return `hsl(${hue}, 70%, 50%)`;
    }
  };

  return (
    <Box position="relative" sx={{ overflow: 'hidden' }}>
      <ComposableMap
        projection="geoAlbersUsa"
        width={800}
        height={600}
        style={{ width: '100%', height: `${height}px` }}
      >
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const stateCode = geo.id; // US Atlas uses FIPS codes
              const stateName = geo.properties.name;
              const stateInfo = stateData[stateCode] || stateData[stateName];
              const value = stateInfo?.value;
              const fillColor = getColor(value);

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={fillColor}
                  stroke="#FFFFFF"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none' },
                    hover: { 
                      outline: 'none', 
                      fill: fillColor,
                      opacity: 0.8,
                      cursor: 'pointer',
                    },
                    pressed: { outline: 'none' },
                  }}
                  onMouseEnter={() => {
                    if (stateInfo) {
                      const tooltip = `${stateInfo.label}: ${stateInfo.currency}${value.toLocaleString()}${stateInfo.transactions ? ` (${stateInfo.transactions} txns)` : ''}`;
                      setTooltipContent(tooltip);
                    } else {
                      setTooltipContent(`${stateName}: No data`);
                    }
                  }}
                  onMouseLeave={() => setTooltipContent('')}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {tooltipContent && (
        <Box
          position="absolute"
          top={10}
          left="50%"
          sx={{
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '14px',
            pointerEvents: 'none',
            zIndex: 1000,
            whiteSpace: 'nowrap',
          }}
        >
          {tooltipContent}
        </Box>
      )}

      {/* Legend */}
      <Box
        position="absolute"
        bottom={10}
        right={10}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '8px',
          borderRadius: '4px',
          fontSize: '12px',
        }}
      >
        <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
          Spending
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box width={15} height={15} bgcolor="hsl(120, 70%, 80%)" border="1px solid #ccc" />
          <Typography variant="caption">Low</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box width={15} height={15} bgcolor="hsl(60, 70%, 50%)" border="1px solid #ccc" />
          <Typography variant="caption">Medium</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box width={15} height={15} bgcolor="hsl(0, 70%, 50%)" border="1px solid #ccc" />
          <Typography variant="caption">High</Typography>
        </Box>
      </Box>
    </Box>
  );
}
